import { useState, useMemo, useContext, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import planApi from '@api/planApi';
import { normalizePage } from '@utils/paging';
import { AuthContext } from '@context';

export default function useDeliverablePage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);
  const location = useLocation();

  const passedBizName = location.state?.searchBizName || '';
  
  const [filters, setFilters] = useState({ 
      assignee: '', 
      companyName: passedBizName, 
      startDate: '', 
      endDate: '', 
      pageNum: 1, 
      pageSize: 10 
  });

  useEffect(() => {
    if (location.state?.searchBizName) {
      // 검색어를 받았으니 history state를 비워줍니다.
      window.history.replaceState({}, document.title)
    }
  }, [location.state]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // 상단 그룹 목록 조회
  const groupQuery = useQuery({
    queryKey: ['plan', 'deliverables', 'groups', filters],
    queryFn: () => planApi.listDeliverableGroups(filters),
    keepPreviousData: true,
  });

  const page = useMemo(() => normalizePage(groupQuery.data) || { rows: [], total: 0, pageNum: 1, pageSize: 10 }, [groupQuery.data]);

  // 하단 디테일 파일 목록 조회
  const detailQuery = useQuery({
    queryKey: ['plan', 'deliverables', 'details', selectedGroup?.bizMgtNo, selectedGroup?.staffId1],
    queryFn: () => planApi.listDeliverableDetails(selectedGroup.bizMgtNo, selectedGroup.staffId1),
    enabled: !!selectedGroup,
  });

  // 파일 업로드
  const uploadMutation = useMutation({
    mutationFn: ({ assignNo, files }) => planApi.uploadDeliverableFile(assignNo, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables', 'details'] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'progress'] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'dashboard'] });
    }
  });

  // 🌟 (분리) 1. 일괄 삭제 Mutation
  const deleteAllMutation = useMutation({
    mutationFn: ({ assignNo, atchFileId }) => planApi.deleteAllDeliverableFiles(assignNo, atchFileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables', 'details'] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'progress'] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'dashboard'] });
    }
  });

  // 🌟 (분리) 2. 개별 삭제 Mutation
  const deleteSingleMutation = useMutation({
    mutationFn: ({ assignNo, atchFileId, fileSn }) => planApi.deleteSingleDeliverableFile(assignNo, atchFileId, fileSn),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables', 'details'] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'progress'] });
      queryClient.invalidateQueries({ queryKey: ['plan', 'dashboard'] });
    }
  });

  const downloadFile = async (atchFileId, orignlFileNm) => {
    try {
      const response = await planApi.downloadDeliverableFile(atchFileId);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', orignlFileNm); // 🌟 원본 파일명 지정
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url); // 메모리 해제
    } catch (error) {
      alert("파일 다운로드 중 오류가 발생했습니다.");
    }
  };

  // 🌟 (추가) 단일 파일 다운로드 핸들러
  const downloadSingleFile = async (atchFileId, fileSn, orignlFileNm) => {
    try {
      const response = await planApi.downloadSingleDeliverableFile(atchFileId, fileSn);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', orignlFileNm); // 🌟 원본 파일명 지정
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("개별 파일 다운로드 중 오류가 발생했습니다.");
    }
  };

  const downloadAllFiles = async () => {
    try {
      const response = await planApi.downloadAllDeliverables(filters);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '전체_산출물_다운로드.zip');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("전체 파일 다운로드 중 오류가 발생했습니다.");
    }
  };

  // 🌟 (추가) 속도 조절이 가능한 커스텀 스크롤 함수
  const customSmoothScroll = (ref, duration = 800) => {
    if (!ref.current) return;

    // 브라우저 창 기준 타겟의 위치 + 현재 스크롤 위치 (목표 지점)
    const targetPosition = ref.current.getBoundingClientRect().top + window.scrollY;
    // 현재 스크롤 위치
    const startPosition = window.scrollY;
    // 이동해야 할 거리 (목표 지점 - 현재 지점)
    const distance = targetPosition - startPosition;
    let startTime = null;

    // Easing 함수 (점점 빠르게 가다가 끝에서 부드럽게 느려지는 효과 - easeInOutQuad)
    const ease = (t, b, c, d) => {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    };

    // 애니메이션 실행 함수
    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      
      // 현재 시간에 맞는 스크롤 위치 계산
      const run = ease(timeElapsed, startPosition, distance, duration);
      window.scrollTo(0, run);

      // 설정한 시간(duration)이 다 될 때까지 반복
      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      } else {
        // 애니메이션이 끝나면 혹시 모를 오차를 맞추기 위해 타겟 위치로 픽스
        window.scrollTo(0, targetPosition);
      }
    };

    requestAnimationFrame(animation);
  };

  const onSearch = () => { setFilters(p => ({ ...p, pageNum: 1 })); setSelectedGroup(null); };

  return {
    filters, setFilters, page, onSearch,
    selectedGroup, setSelectedGroup,
    detailList: detailQuery.data || [], detailLoading: detailQuery.isFetching,
    uploadFile: uploadMutation.mutateAsync, deleteAllFiles: deleteAllMutation.mutateAsync, 
    deleteSingleFile: deleteSingleMutation.mutateAsync, downloadFile, downloadAllFiles, downloadSingleFile, customSmoothScroll,
    auth
  };
}